import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import { Add, Edit, Delete, Visibility } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getSystems, deleteSystem } from '../../services/systemService';
import { getRelativeFileUrl } from '../../services/relativeFilesService';
import useAuth from '../../hooks/useAuth';
import swal from 'sweetalert';
import logoPlaceholder from '../../assets/placeholder.jpg';

interface System {
  id: string;
  name: string;
  description?: string;
  base_url: string;
  logo_url?: string; // Campo opcional para a URL da logo
}

const SystemsList: React.FC = () => {
  const [systems, setSystems] = useState<System[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  const fetchSystems = async () => {
    setLoading(true);
    try {
      const data = await getSystems();
      setSystems(data);
    } catch (error) {
      console.error('Erro ao buscar sistemas:', error);
      swal('Erro', 'Não foi possível buscar os sistemas.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSystems();
  }, []);

  const handleDelete = async (systemId: string) => {
    const confirm = await swal({
      title: 'Tem certeza?',
      text: 'Você tem certeza que deseja deletar este sistema?',
      icon: 'warning',
      buttons: ['Cancelar', 'Deletar'],
      dangerMode: true,
    });

    if (confirm) {
      try {
        await deleteSystem(systemId);
        swal('Deletado!', 'Sistema deletado com sucesso.', 'success');
        fetchSystems(); // Atualiza a lista após deleção
      } catch (error) {
        console.error('Erro ao deletar sistema:', error);
        swal('Erro', 'Não foi possível deletar o sistema.', 'error');
      }
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5">Sistemas</Typography>
        {user?.is_system_admin && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={() => navigate('/sistemas/novo')}
          >
            Adicionar Sistema
          </Button>
        )}
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              {/* Coluna para a logo */}
              <TableCell>Logo</TableCell>
              <TableCell>Nome</TableCell>
              <TableCell>Descrição</TableCell>
              <TableCell>URL Base</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {systems.map((system) => (
              <TableRow key={system.id}>
                <TableCell>
                  {system.logo_url ? (
                    <Box
                      component="img"
                      src={getRelativeFileUrl(system.logo_url)}
                      alt={`${system.name} Logo`}
                      sx={{
                        width: 40,
                        height: 40,
                        objectFit: 'contain',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                      }}
                    />
                  ) : (
                    <Box
                      component="img"
                      src={logoPlaceholder}
                      alt="Logo Padrão"
                      sx={{
                        width: 40,
                        height: 40,
                        objectFit: 'contain',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                      }}
                    />
                  )}
                </TableCell>
                <TableCell>{system.name}</TableCell>
                <TableCell>{system.description || '—'}</TableCell>
                <TableCell>
                  <a href={system.base_url} target="_blank" rel="noopener noreferrer">
                    {system.base_url}
                  </a>
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Visualizar">
                    <IconButton onClick={() => navigate(`/sistemas/${system.id}`)}>
                      <Visibility />
                    </IconButton>
                  </Tooltip>
                  {user?.is_system_admin && (
                    <>
                      <Tooltip title="Editar">
                        <IconButton onClick={() => navigate(`/sistemas/${system.id}/editar`)}>
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Deletar">
                        <IconButton onClick={() => handleDelete(system.id)}>
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {systems.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  Nenhum sistema encontrado.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default SystemsList;